// Messaging (org panel) UI state. Conversation/message data lives in the
// `chat` module; this module keeps the panel state (active conversation,
// search, new-chat picker, agent catalog) and adapts `chat` conversations to
// the peer-centric shape the panel components consume.
import { API, HOST } from '@/utils/http'

export default {
  namespaced: true,
  state: () => ({
    activeKey: null, // conversation uuid open in the panel
    agents: [],
    agentsLoaded: false,
    // The search input lives in MessagingPanel but the results render in
    // ChatList, so the query lives here rather than in either component.
    searchQuery: '',
    newChatOpen: false,
  }),
  getters: {
    // DM conversations in the panel's shape: key + kind ('member'|'agent') + peer.
    conversations: (state, getters, rootState, rootGetters) =>
      rootGetters['chat/conversations']
        .filter((c) => c.kind === 'dm' && c.peer)
        .map((c) => ({
          key: c.uuid,
          kind: c.peerKind,
          peer: c.peer,
          lastMessage: c.lastMessage,
          unreadCount: c.unreadCount,
          updatedAt: c.lastAt,
          messages: rootGetters['chat/flatMessages'](c.uuid),
        })),
    conversation: (state, getters) => (key) =>
      getters.conversations.find((c) => c.key === key) || null,
    messages: (state, getters, rootState, rootGetters) => (key) =>
      rootGetters['chat/flatMessages'](key),
    activeKey: (state) => state.activeKey,
    activeConversation: (state, getters) =>
      state.activeKey ? getters.conversation(state.activeKey) : null,
    pendingAgent: (state, getters, rootState, rootGetters) => (key) =>
      rootGetters['chat/pendingAgent'](key),
    agents: (state) => state.agents,
    searchQuery: (state) => state.searchQuery,
    newChatOpen: (state) => state.newChatOpen,
  },
  mutations: {
    SET_ACTIVE(state, key) {
      state.activeKey = key
    },
    SET_AGENTS(state, agents) {
      state.agents = agents
      state.agentsLoaded = true
    },
    SET_SEARCH_QUERY(state, query) {
      state.searchQuery = query
    },
    SET_NEW_CHAT_OPEN(state, open) {
      state.newChatOpen = open
      state.searchQuery = ''
    },
  },
  actions: {
    // Every agent the org can start a DM with; the per-project roster is a
    // separate, narrower call.
    async fetchAgents({ state, commit, rootGetters }) {
      if (state.agentsLoaded) return
      try {
        const orgId = rootGetters['org/getSelected']
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project-agents/catalog`,
        })
        commit('SET_AGENTS', response.data)
      } catch (e) {
        commit('SET_AGENTS', [])
      }
    },
    async openConversation({ commit, dispatch }, { kind, peer }) {
      const uuid = await dispatch(
        'chat/openDm',
        kind === 'agent' ? { peerAgentId: peer.agent_id } : { peerMemberId: peer.member_id },
        { root: true }
      )
      commit('SET_ACTIVE', uuid)
      dispatch('chat/markRead', uuid, { root: true }).catch(() => {})
      return uuid
    },
    async sendMessage({ dispatch }, { key, text, attachments }) {
      return dispatch(
        'chat/sendMessage',
        { uuid: key, text, attachments },
        { root: true }
      )
    },
    // Agent replies are triggered server-side; this only shows the typing
    // indicator until the reply lands over WS.
    notePendingAgent({ getters, dispatch }, { key }) {
      const conversation = getters.conversation(key)
      if (!conversation || conversation.kind !== 'agent') return
      dispatch(
        'chat/notePendingAgent',
        { uuid: key, name: conversation.peer.display_name },
        { root: true }
      )
    },
  },
}
